import { AppFrameWide } from "./AppFrameWide";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppTokens - THE ONE SOURCE OF WACommander's DESIGN TOKENS (owner-approved design system, 2026-09-30: "1. Do the
// kalmstrom colors ... 14. Yes a written style guide"). Habitat-free: no DOM, so the Bridge suite, the style-guide
// generator (`documentation/design/tools/build-style-guide.ts`) and the app all read the same values.
//
// ⭐ WHAT IS HERE: the colours (light and dark), the type scale (12/13/14/16/20/24), two weights (400/600), the spacing
// steps (4/8/12/16/24/32), the radii (4/6/8, and 999 for what is truly round) and the font stacks. `Css()` writes them as `:root` custom properties.
//
// ⛔ app.css IS STATIC, SO THE BLOCK IS COPIED INTO IT AND A TEST KEEPS THE COPY TRUE. `DesignSystem.test.ts` (Bridge)
// fails when the text between `/* TOKENS:BEGIN` and `TOKENS:END */` in `App/app.css` is not exactly `Css()`, and prints
// the expected block - so an edit here is one paste away from app.css, and a hand edit there is caught. No `<style>`
// element and no inline style carries it (the CSP refuses both).
//
// ⭐ THEMES: light by default; dark when the system asks (`prefers-color-scheme: dark`) UNLESS the owner chose light
// (`:root:not([data-theme="light"])`), and dark whenever the owner chose it (`:root[data-theme="dark"]`, set by
// `AppTheme` from `localStorage` "wac.theme"). The contact frame follows through `FrameCss()` (the page's own tokens
// are re-declared under `html[data-wac-embedded]`, which outranks the page's `:root`).
//
// ⭐ OLD NAMES KEEP WORKING: app.css and the frame sheets were written against `--ink`, `--dim`, `--bg`, `--soft`, `--acc`,
// `--ai`, `--live`, `--owed` ... Each is an ALIAS of a new token (`Aliases`), so nothing that reads an old name breaks.
// ⚠ `--bg` was always the CARD surface (white) and `--soft` the page ground - the aliases keep those meanings.
//
// ⛔ COLOUR RULES (the style guide repeats them): the kalmstrom blue `--accent` is for actions and the AI - Send
// included; `--success`/`--warning`/`--danger` carry meaning, never decoration. Every text pair in `Pairs` is at least
// 4.5:1 in both themes (`DesignSystem.test.ts` measures them).
//
// ⛔ THE BUSINESS LOOK (owner, 2026-10-13: "looks like a less professional version of WhatsApp"): NO WHATSAPP COLOUR
// ANYWHERE - no green Send (the `--send` tokens are gone), no beige wallpaper, no green bubbles. The conversation is
// drawn from the app's own tokens (`FrameVars`: ground = `page`, their words = `surface` with a `line` edge, the
// owner's = `accent-soft` with an `accent-line` edge). ⛔ SQUARE CONTROLS: the radii are 4 / 6 / 8px; `--radius-pill`
// is ONLY for what is round by nature (a contact's disc, a status dot, a task's tick, a spinner, a progress track).

/** One colour token: its custom-property name (without `--`), its light and dark values, and what it is for. */
export class AppColourToken {
    public Name: string = "";
    public Light: string = "";
    public Dark: string = "";
    public Role: string = "";
}

/** A text colour over a ground that must read at 4.5:1 or better, in both themes. */
export class AppContrastPair {
    public Text: string = "";
    public Ground: string = "";
    public Where: string = "";
}

/** One step of a scale: its custom-property name and value. */
export class AppScaleStep {
    public Name: string = "";
    public Value: string = "";
}

export class AppTokens {
    /** The markers around the generated block in app.css. */
    public static readonly Begin: string = "/* TOKENS:BEGIN - generated by AppTokens.Css() in App/src/AppTokens.ts. Edit there, never here. */";
    public static readonly End: string = "/* TOKENS:END */";

    /** The system UI stack - everything but the wordmark. */
    public static readonly UiFont: string = "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, Helvetica, Arial, sans-serif";
    /** The kalmstrom brand face - ONLY the wordmark. */
    public static readonly BrandFont: string = "Verdana, \"Segoe UI\", Calibri, Arial, sans-serif";
    public static readonly MonoFont: string = "ui-monospace, \"Cascadia Mono\", SFMono-Regular, Menlo, Consolas, monospace";

    // DOCUMENTED EXCEPTION (every table below): constant data read while the class loads - no guard, no try/catch.
    // ERROR-SUPPRESSED-JUSTIFIED: literals; nothing here can throw.

    public static readonly Colours: AppColourToken[] = AppTokens.Table([
        ["text", "#2b2f36", "#e6e9ef", "Body text and headings"],
        // ⚠ `muted` WAS #6b7280 (audit 2026-10-01): 4.8:1 on white, but 4.3:1 on the accent tint (a lit row, the palette's
        // lit option), 4.4:1 on a message's green and under 4.5:1 on the page ground - measured as rendered. #5c6370
        // reads at 4.5:1 or better on every ground the app puts it on (`Pairs`).
        ["muted", "#5c6370", "#9aa3b6", "Secondary text: times, counts, hints"],
        ["page", "#f6f7f9", "#0f141c", "The page ground behind the cards"],
        ["surface", "#ffffff", "#161b26", "Cards, panels, inputs"],
        ["line", "#e3e6ec", "#2a3140", "Borders and dividers"],
        ["line-strong", "#858c99", "#6b7385", "The edge of a field or an empty tick - 3:1 against its ground"],
        ["accent", "#095495", "#6ea8e0", "Actions, links, focus, the AI (kalmstrom blue)"],
        ["accent-hover", "#07477e", "#8fbdea", "A primary button under the pointer"],
        ["accent-soft", "#eaf2f9", "#15263a", "The AI panel's tint, a selected chip"],
        ["accent-line", "#c7dcef", "#26466b", "Borders of accent-soft surfaces"],
        ["on-accent", "#ffffff", "#0f141c", "Text on an accent fill"],
        ["success", "#4f8035", "#7fb267", "Done, saved, connected"],
        ["success-soft", "#eef5ea", "#16261a", "A success notice's ground"],
        ["success-line", "#cfe3c4", "#2c4a24", "A success notice's border"],
        ["success-deep", "#3b6128", "#a6d08f", "Text on success-soft"],
        ["warning", "#a5561b", "#e49b5c", "Needs attention: a reply owed, a partial answer"],
        ["warning-soft", "#fdf3e3", "#2e2415", "A warning notice's ground"],
        ["warning-line", "#f0dfc0", "#4a3a1d", "A warning notice's border"],
        ["warning-ink", "#7a4a05", "#f0d9a8", "Text on warning-soft"],
        ["danger", "#b42318", "#f28b82", "Refused, overdue, destructive"],
        ["danger-soft", "#fdece9", "#3a1e1b", "A danger notice's ground"],
        ["danger-line", "#f3cfc8", "#5a2b25", "A danger notice's border"],
        ["mark", "#fbe7a1", "#5c4a14", "A search hit's highlight"],
        ["on-mark", "#2b2f36", "#fff3cf", "Text inside a highlight"],
        // ⚠ `mebubble` WAS a pale green (#e3f5e9 / #16372a). It is the accent tint now - the same ground as the owner's
        // bubble in the conversation - and is always drawn with an `accent-line` edge.
        ["mebubble", "#eaf2f9", "#15263a", "The owner's own words drawn as a message (a draft) - the accent tint"],
        ["disc", "#dfe3ee", "#2b3242", "A contact's disc before its picture"],
        ["disc-ink", "#5b6474", "#9aa3b6", "Initials and the silhouette on a disc"],
        ["viz1", "#2a78d6", "#3987e5", "Chart series 1"],
        ["viz2", "#eb6834", "#d95926", "Chart series 2"],
        ["viz3", "#1baf7a", "#199e70", "Chart series 3"],
        ["viz4", "#eda100", "#c98500", "Chart series 4"],
        ["viz5", "#e87ba4", "#d55181", "Chart series 5"],
        ["viz6", "#008300", "#008300", "Chart series 6"],
    ]);

    /**
     * The old names, each pointing at a new token (`[old, new]`). ⛔ Kept so nothing written against them breaks - the
     * contact frame's sheets (`AppReactions.Css` and friends) and many app.css rules read these.
     */
    public static readonly Aliases: string[][] = [
        ["ink", "text"], ["dim", "muted"], ["bg", "surface"], ["soft", "page"], ["quiet", "muted"],
        ["acc", "accent"], ["ai", "accent"], ["aiink", "accent"], ["aisoft", "accent-soft"], ["ailine", "accent-line"],
        ["live", "success"], ["owed", "warning"],
        ["brandsoft", "success-soft"], ["brandline", "success-line"], ["branddeep", "success-deep"],
        ["warnbg", "warning-soft"], ["warnink", "warning-ink"], ["warnline", "warning-line"],
        ["badbg", "danger-soft"], ["badink", "danger"], ["badline", "danger-line"],
    ];

    /** Every text-over-ground pair the app draws, each measured at 4.5:1 or better in both themes. */
    public static readonly Pairs: AppContrastPair[] = AppTokens.PairTable([
        ["text", "page", "Body text on the page"],
        ["text", "surface", "Body text on a card"],
        ["muted", "surface", "Hints and times on a card"],
        ["muted", "page", "Hints on the page"],
        ["muted", "accent-soft", "Hints on a lit row or a lit option"],
        ["muted", "mebubble", "A time beside the owner's own words"],
        ["muted", "warning-soft", "Hints on a warning notice"],
        ["accent", "surface", "A link or a text button on a card"],
        ["accent", "page", "A link on the page"],
        ["accent", "accent-soft", "AI text and chips on their tint"],
        ["on-accent", "accent", "A primary button's words"],
        ["text", "accent-soft", "The owner's own words in the conversation (their bubble is the accent tint)"],
        ["danger", "accent-soft", "\"Not sent\" on the owner's own bubble"],
        ["warning", "accent-soft", "\"Not confirmed\" on the owner's own bubble"],
        ["success", "surface", "A success word on a card"],
        ["success-deep", "success-soft", "A success notice"],
        ["warning", "surface", "A warning word on a card"],
        ["warning-ink", "warning-soft", "A warning notice"],
        ["danger", "surface", "A refusal on a card"],
        ["danger", "danger-soft", "A danger notice"],
        ["on-mark", "mark", "A highlighted search hit"],
        ["text", "mebubble", "A draft drawn as a message"],
        ["text", "warning-soft", "An account warning's sentence"],
        ["text", "danger-soft", "An account warning's sentence when nothing is sent or received"],
    ]);

    public static readonly TypeScale: AppScaleStep[] = AppTokens.Steps([
        ["text-xs", "12px"], ["text-sm", "13px"], ["text-base", "14px"], ["text-md", "16px"], ["text-lg", "20px"], ["text-xl", "24px"],
    ]);
    public static readonly Weights: AppScaleStep[] = AppTokens.Steps([["weight-regular", "400"], ["weight-strong", "600"]]);
    public static readonly Spacing: AppScaleStep[] = AppTokens.Steps([
        ["space-1", "4px"], ["space-2", "8px"], ["space-3", "12px"], ["space-4", "16px"], ["space-5", "24px"], ["space-6", "32px"],
    ]);
    public static readonly Radii: AppScaleStep[] = AppTokens.Steps([
        ["radius-sm", "4px"], ["radius-md", "6px"], ["radius-lg", "8px"], ["radius-pill", "999px"],
    ]);

    /**
     * Compact declarations for documentation panes using the complete product colour table.
     * DOCUMENTED EXCEPTION: pure renderer, including when the global error floor is enabled.
     * ERROR-PROPAGATION-JUSTIFIED: preserve native token getter faults for each tool's caller.
     */
    public static ThemeVariables = (Dark: boolean): string => {
        const Out: string[] = [];
        for (const One of AppTokens.Colours) { Out.push("--" + One.Name + ":" + (Dark ? One.Dark : One.Light)); }
        return Out.join(";") + ";color-scheme:" + (Dark ? "dark" : "light");
    };

    /** Builds the colour table. DOCUMENTED EXCEPTION: runs once while the class loads. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    private static Table(Rows: string[][]): AppColourToken[] {
        const Out: AppColourToken[] = [];
        for (const Row of Rows) {
            const One: AppColourToken = new AppColourToken();
            One.Name = Row[0];
            One.Light = Row[1];
            One.Dark = Row[2];
            One.Role = Row[3];
            Out.push(One);
        }
        return Out;
    }

    /** Builds the pair table. DOCUMENTED EXCEPTION: runs once while the class loads. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    private static PairTable(Rows: string[][]): AppContrastPair[] {
        const Out: AppContrastPair[] = [];
        for (const Row of Rows) {
            const One: AppContrastPair = new AppContrastPair();
            One.Text = Row[0];
            One.Ground = Row[1];
            One.Where = Row[2];
            Out.push(One);
        }
        return Out;
    }

    /** Builds a scale. DOCUMENTED EXCEPTION: runs once while the class loads. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    private static Steps(Rows: string[][]): AppScaleStep[] {
        const Out: AppScaleStep[] = [];
        for (const Row of Rows) {
            const One: AppScaleStep = new AppScaleStep();
            One.Name = Row[0];
            One.Value = Row[1];
            Out.push(One);
        }
        return Out;
    }

    // ── Lookup ───────────────────────────────────────────────────────────────────────────────

    /** A colour token by name, or null. */
    public static Find = (Name: string): AppColourToken | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const One of AppTokens.Colours) { if (One.Name === Name) { return One; } }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.Find");
            return null;
        }
    };

    /** A token's value in one theme ("" for an unknown name). */
    public static ValueOf = (Name: string, Dark: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const One: AppColourToken | null = AppTokens.Find(Name);
            if (One === null) { return ""; }
            if (Dark === true) { return One.Dark; }
            return One.Light;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.ValueOf");
            return "";
        }
    };

    // ── Contrast (WCAG 2.x) ──────────────────────────────────────────────────────────────────

    /** The relative luminance of a `#rrggbb` colour (WCAG 2.x), or -1 for anything else. */
    public static Luminance = (Hex: string): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            const Text: string = nz(Hex).trim().toLowerCase();
            if (/^#[0-9a-f]{6}$/.test(Text) === false) { return -1; }
            let Sum: number = 0;
            const Weights: number[] = [0.2126, 0.7152, 0.0722];
            for (let At: number = 0; At < 3; At = At + 1) {
                Sum = Sum + Weights[At] * AppTokens.Linear(parseInt(Text.substring(1 + At * 2, 3 + At * 2), 16) / 255);
            }
            return Sum;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.Luminance");
            return -1;
        }
    };

    /** One sRGB channel (0..1) to linear light. */
    private static Linear = (Channel: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (Channel <= 0.03928) { return Channel / 12.92; }
            return Math.pow((Channel + 0.055) / 1.055, 2.4);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.Linear");
            return 0;
        }
    };

    /** The contrast ratio of two `#rrggbb` colours (1..21), or 0 when either cannot be read. */
    public static Contrast = (A: string, B: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const La: number = AppTokens.Luminance(A);
            const Lb: number = AppTokens.Luminance(B);
            if (La < 0 || Lb < 0) { return 0; }
            return (Math.max(La, Lb) + 0.05) / (Math.min(La, Lb) + 0.05);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.Contrast");
            return 0;
        }
    };

    /** A pair's ratio in one theme. */
    public static PairRatio = (Pair: AppContrastPair, Dark: boolean): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return AppTokens.Contrast(AppTokens.ValueOf(Pair.Text, Dark), AppTokens.ValueOf(Pair.Ground, Dark));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.PairRatio");
            return 0;
        }
    };

    // ── The stylesheet block ─────────────────────────────────────────────────────────────────

    /** One theme's colour declarations, one per line, indented. */
    private static ColourLines = (Dark: boolean, Indent: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            let Scheme: string = "light";
            if (Dark === true) { Scheme = "dark"; }
            Out.push(Indent + "color-scheme: " + Scheme + ";");
            for (const One of AppTokens.Colours) {
                let Value: string = One.Light;
                if (Dark === true) { Value = One.Dark; }
                Out.push(Indent + "--" + One.Name + ": " + Value + ";");
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.ColourLines");
            return [];
        }
    };

    /** The scales, the font stacks, the focus ring and the aliases - theme-free, so written once. */
    private static FixedLines = (Indent: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            for (const Scale of [AppTokens.TypeScale, AppTokens.Weights, AppTokens.Spacing, AppTokens.Radii]) {
                for (const One of Scale) { Out.push(Indent + "--" + One.Name + ": " + One.Value + ";"); }
            }
            Out.push(Indent + "--font-ui: " + AppTokens.UiFont + ";");
            Out.push(Indent + "--font-brand: " + AppTokens.BrandFont + ";");
            Out.push(Indent + "--font-mono: " + AppTokens.MonoFont + ";");
            Out.push(Indent + "--focus-ring: 2px solid var(--accent);");
            for (const One of AppTokens.Aliases) { Out.push(Indent + "--" + One[0] + ": var(--" + One[1] + ");"); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.FixedLines");
            return [];
        }
    };

    /**
     * The block app.css carries between the markers, markers included - light on `:root`, dark for the system's dark
     * mode unless the owner chose light, and dark whenever the owner chose it.
     */
    public static Css = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [AppTokens.Begin, ":root {"];
            Out.push(...AppTokens.ColourLines(false, "    "));
            Out.push(...AppTokens.FixedLines("    "));
            Out.push("}", "@media (prefers-color-scheme: dark) {", "    :root:not([data-theme=\"light\"]) {");
            Out.push(...AppTokens.ColourLines(true, "        "));
            Out.push("    }", "}", ":root[data-theme=\"dark\"] {");
            Out.push(...AppTokens.ColourLines(true, "    "));
            Out.push("}", AppTokens.End);
            return Out.join("\n");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.Css");
            return "";
        }
    };

    /** The text between the markers in a stylesheet (markers included), or "" when either is missing. */
    public static BlockIn = (Sheet: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Text: string = nz(Sheet).split("\r\n").join("\n");
            const From: number = Text.indexOf(AppTokens.Begin);
            const To: number = Text.indexOf(AppTokens.End);
            if (From < 0 || To < From) { return ""; }
            return Text.substring(From, To + AppTokens.End.length);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.BlockIn");
            return "";
        }
    };

    // ── The contact frame ────────────────────────────────────────────────────────────────────

    /**
     * The contact page's own tokens mapped onto ours, for the frame sheet list (`AppMain.Frame`). The page (a record the
     * service writes, `DossierHtml`) declares `--ink`, `--dim`, `--bg` ... on `:root` and darkens them by the system's
     * setting alone; re-declared here under `html[data-wac-embedded]` (which outranks `:root`), the frame follows the
     * app's palette AND the owner's theme choice, while a page opened on its own is untouched.
     * ⭐ THE BUSINESS LOOK (2026-10-13): the chat's ground and bubbles are the app's tokens too (`FrameVars`), and
     * `FrameFlat` gives every ALREADY-BUILT page the flat bubble, the plain day label and the square chips - no rebuild.
     */
    public static FrameCss = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Sel: string = "html[data-wac-embedded]";
            return Sel + "{" + AppTokens.FrameVars(false) + "}"
                + "@media(prefers-color-scheme:dark){" + Sel + ":not([data-theme=\"light\"]){" + AppTokens.FrameVars(true) + "}}"
                + Sel + "[data-theme=\"dark\"]{" + AppTokens.FrameVars(true) + "}"
                + Sel + " .b a{color:var(--acc)}"
                + AppTokens.FrameCalm(Sel) + AppTokens.FrameFlat(Sel);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.FrameCss");
            return "";
        }
    };

    /**
     * ⭐ CALM SENTENCE-CASE HEADINGS IN THE FRAME TOO (owner, 2026-09-30): the page's section heads, drawer head, fact
     * labels and day labels are written in capitals with wide tracking - here they read as written, semibold. And the
     * day label's typed ▸ / ▾ becomes a drawn chevron (two borders turned), pointing right when folded, down when open.
     */
    private static FrameCalm = (Sel: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Chevron: string = "content:\"\";display:inline-block;width:5px;height:5px;margin:0 9px 2px 1px;"
                + "border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;";
            return Sel + " h2," + Sel + " .drawerhead," + Sel + " .lab," + Sel + " .st," + Sel + " .dayhead span"
                + "{text-transform:none;letter-spacing:0}"
                + Sel + " h2{font-size:14px;font-weight:600;color:var(--ink)}"
                + Sel + " .drawerhead{font-size:14px;color:var(--ink)}"
                + Sel + " .lab{font-size:12px;font-weight:600}"
                + Sel + " .dayhead span{font-size:12px;font-weight:600}"
                + Sel + " .dayhead span::before{" + Chevron + "transform:rotate(-45deg)}"
                + Sel + " .daybox:checked ~ .dayhead span::before," + Sel + " .day:has(.b:target) .dayhead span::before"
                + "{" + Chevron + "transform:rotate(45deg)}"
                // ⭐ The page's header pills and the reply-owed disc hard-code LIGHT grounds (dark only by the system's
                // setting): drawn from tokens here, they follow the owner's chosen theme as well.
                + Sel + " .pill{background:var(--line)}" + Sel + " .pill.owed{background:var(--warning-soft);color:var(--warning)}"
                + Sel + " .tcount{background:var(--danger-soft);color:var(--danger)}"
                + Sel + " .tk.rp .tb{background:var(--accent-soft);color:var(--accent)}"
                + Sel + " .tmore," + Sel + " .tk .tgo:hover{color:var(--accent)}";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.FrameCalm");
            return "";
        }
    };

    /**
     * ⭐ THE FLAT CONVERSATION (owner, 2026-10-13 - the business look; mockup `documentation/mockups/business-look.html`).
     * Every rule is under `html[data-wac-embedded]`, so it outranks the page's own `.b` / `.dayhead span` / `.rx` rules
     * whatever the page's age: a bubble is a 1px-edged rectangle with ONE 6px radius on all four corners (the page
     * squares the corner nearest its side) and no shadow; the owner's is the accent tint with the accent edge; a group
     * sender's name is the accent; the day label is plain words between two hairlines; a reaction badge, the header's
     * chips and the task strip's chips are small-radius rectangles with an edge instead of a shadow.
     * ⚠ `.b:target` is restated: the bubble's `box-shadow:none` here would otherwise take the linked message's ring away.
     */
    private static FrameFlat = (Sel: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ `border-box` + the 10px rule: a bubble that can be reacted to has a 44px floor (`AppReactions.Css`), which
            // as a CONTENT height made a one-line message 58px tall with its time hanging under the words. The floor now
            // counts the padding and the edge, and a plain text bubble centres its one line in it, the time on that line.
            return Sel + " .b{box-sizing:border-box;border:1px solid var(--line);border-radius:6px;box-shadow:none}"
                // ⭐ Only where the floor applies (`AppReactions.Css`: a coarse pointer, or a phone-width frame that is not in
                // the wide layout - `AppFrameWide`); elsewhere a one-line bubble is the page's own 34px. A task's source
                // bubble is no exception any more (`AppTaskFrame.Css` reserves no space for "Mark done").
                + "@media(pointer:coarse){" + AppTokens.FrameFloor(Sel) + "}"
                // A phone: a bubble may take more of the narrow column, so a short line keeps its time beside it.
                + "@media(max-width:620px){" + AppTokens.FrameFloor(Sel + AppFrameWide.Not) + Sel + AppFrameWide.Not + " .b{max-width:86%}}"
                + Sel + " .b.them{border-top-left-radius:6px}"
                + Sel + " .b.me{border-color:var(--accent-line);border-top-right-radius:6px}"
                + Sel + " .b:target{box-shadow:0 0 0 2px var(--accent)}"
                + Sel + " .b .who{color:var(--accent)}"
                + Sel + " .b img.pic{border-radius:4px}"
                + Sel + " .dayhead{gap:12px}"
                + Sel + " .dayhead::before," + Sel + " .dayhead::after{content:\"\";flex:1;height:1px;background:var(--line)}"
                + Sel + " .dayhead span{background:var(--daybg);border:0;box-shadow:none;border-radius:4px;padding:2px 8px}"
                + Sel + " .b .rx{box-sizing:border-box;background:var(--surface);border:1px solid var(--line);border-radius:4px;box-shadow:none}"
                + Sel + " .pill," + Sel + " .openbtn .n," + Sel + " .tk .due," + Sel + " .tk .who," + Sel + " .tk.rp .tb," + Sel + " .st{border-radius:4px}"
                + Sel + " .openbtn{border-radius:6px}"
                + Sel + " .tcount{border-radius:4px;box-shadow:inset 0 0 0 1px var(--danger-line)}"
                + Sel + " .tk{border-top-color:var(--line)}"
                + Sel + " section," + Sel + " .detailsbody>details{border-radius:8px}";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.FrameFlat");
            return "";
        }
    };

    /**
     * A plain text bubble under the 44px touch floor: its one line centred, the time on that line.
     * DOCUMENTED EXCEPTION: a constant rule over a selector. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    private static FrameFloor = (Sel: string): string => {
        return Sel + " .b[data-mid]:has(>.txt+.t){padding-top:10px;padding-bottom:10px}" + Sel + " .b[data-mid]:has(>.txt+.t)>.t{bottom:8px}";
    };

    /** The page's token names with this theme's values - ours throughout, the chat's ground and bubbles included. */
    private static FrameVars = (Dark: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Scheme: string = "light";
            if (Dark === true) { Scheme = "dark"; }
            const Names: string[][] = [
                ["ink", "text"], ["dim", "muted"], ["line", "line"], ["bg", "surface"], ["soft", "page"],
                ["live", "success"], ["owed", "warning"], ["quiet", "muted"], ["acc", "accent"],
            ];
            let Out: string = "color-scheme:" + Scheme + ";";
            // Our own tokens too, so the frame rules below can name --danger-soft, --accent ... in either theme.
            for (const One of AppTokens.Colours) { Out = Out + "--" + One.Name + ":" + AppTokens.ValueOf(One.Name, Dark) + ";"; }
            for (const One of Names) { Out = Out + "--" + One[0] + ":" + AppTokens.ValueOf(One[1], Dark) + ";"; }
            // ⭐ THE CHAT'S OWN FIVE VARIABLES ARE OUR TOKENS (2026-10-13; they were WhatsApp's beige, white and greens):
            // the ground is the page, their bubble the surface, the owner's the accent tint, the day label sits on the
            // ground. `--onout` = the small print ON the owner's bubble (the time, "Sent") = `muted`, which `Pairs`
            // measures on `accent-soft` at 4.5:1 or better in both themes.
            const Chat: string[][] = [["chatbg", "page"], ["inbg", "surface"], ["outbg", "accent-soft"], ["daybg", "page"], ["onout", "muted"]];
            for (const One of Chat) { Out = Out + "--" + One[0] + ":" + AppTokens.ValueOf(One[1], Dark) + ";"; }
            if (Dark === true) { return Out + "--actme:#3987e5;--actthem:#d95926;"; }
            return Out + "--actme:#2a78d6;--actthem:#eb6834;";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTokens.FrameVars");
            return "";
        }
    };
}
